import * as DropdownPrimitive from '@radix-ui/react-dropdown-menu'
import { ComponentProps, FC, forwardRef } from 'react'
import { styled } from '../../../../../lib/stitches'
import { Check } from '../../../../core/icons/responsive-icons'

export const Root = DropdownPrimitive.Root
export const Portal = DropdownPrimitive.Portal
export const Trigger = styled(DropdownPrimitive.Trigger, {
  all: 'unset',
  borderRadius: '8px',
  cursor: 'pointer',
  padding: '10px 12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '14px',
  fontWeight: '$medium',
  color: '$gray12',
  borderColor: '$gray03',
  borderStyle: 'solid',
  borderWidth: '1px',
  background: '$gray01',
  boxShadow: '0px 1px 2px 0px #0000000D',
  transition: 'all 100ms ease',
  '&:hover': {
    transform: 'translateY(-1px)',
    borderColor: '$gray10'
  },
  '&:active': {
    transform: 'translateY(1px)',
    borderColor: '$gray14'
  },
  '&:disabled': {
    borderColor: '$gray03',
    color: '$gray05'
  },
  variants: {
    active: {
      true: {
        color: 'rgb(82, 99, 244)'
      }
    }
  }
})

export const Content = styled(DropdownPrimitive.Content, {
  fontFamily: '$dm',
  zIndex: 100,
  background: '$gray01',
  boxSizing: 'border-box',
  padding: '12px',
  borderWidth: '1px',
  borderColor: '$gray03',
  borderStyle: 'solid',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  variants: {
    mobile: {
      true: {
        overflow: 'scroll',
        width: 'calc(100vw - 32px)',
        maxHeight: '60vh',
        flexWrap: 'nowrap'
      },
      false: {
        overflow: 'auto',
        flexWrap: 'wrap'
      }
    }
  }
})

Content.defaultProps = {
  collisionPadding: 16
}

export const Group = styled(DropdownPrimitive.Group, {
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  flexShrink: 0
})

export const Label = styled(DropdownPrimitive.Label, {
  all: 'unset',
  userSelect: 'none',
  cursor: 'default',
  padding: '6px 12px',
  fontSize: '14px',
  lineHeight: '20px',
  color: '$gray15',
  fontWeight: '$bold',
  display: 'flex'
})

export const Item = styled(DropdownPrimitive.Item, {
  all: 'unset',
  cursor: 'pointer',
  userSelect: 'none',
  display: 'flex',
  alignItems: 'center',
  padding: '6px 12px',
  borderRadius: '8px',
  boxSizing: 'border-box',
  fontSize: '14px',
  lineHeight: '20px',
  color: '$gray14',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  background: 'transparent',
  transition: 'all 100ms ease',
  '&:hover': {
    color: '$gray15',
    background: 'rgba(0,0,0,.03)'
  }
})

export const CheckboxItem = styled(DropdownPrimitive.CheckboxItem, {
  all: 'unset',
  cursor: 'pointer',
  userSelect: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '6px 12px',
  borderRadius: '8px',
  boxSizing: 'border-box',
  fontSize: '14px',
  lineHeight: '20px',
  color: '$gray09',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  background: 'transparent',
  transition: 'all 100ms ease',
  '&:hover': {
    color: '$gray15',
    background: 'rgba(0,0,0,.03)'
  },
  '&[data-state="checked"]': {
    background: 'rgba(82, 99, 244, 0.1)',
    color: 'rgb(82, 99, 244)'
  },
  '&:disabled': {
    background: 'transparent',
    pointerEvents: 'none'
  },
  variants: {
    bold: {
      true: {
        fontWeight: '$bold',
        color: '$gray15'
      }
    }
  }
})

const CheckIndicator = styled(DropdownPrimitive.ItemIndicator, {
  width: '16px',
  height: '16px',
  display: 'flex',
  flexShrink: 0
})

const Checkmark = styled(Check, {
  strokeWidth: '1.5px'
})

export const Checkbox: FC<ComponentProps<typeof CheckIndicator>> = forwardRef((props, ref) => (
  <CheckIndicator>
    <Checkmark />
  </CheckIndicator>
))
